/**
 * Phone number formatting helpers (shared, single source of truth).
 *
 * Goal: format US/NANP numbers for DISPLAY as the user types — "(XXX) XXX-XXXX"
 * (or "+1 (XXX) XXX-XXXX" when an 11-digit leading-1 number is present) — while
 * STORING a normalized value (digits only) so downstream dedup/search/delivery
 * stays consistent. International or non-NANP-looking input is passed through
 * unchanged so we never corrupt it.
 */

/** Strip everything except digits. */
export function digitsOnly(value: string | null | undefined): string {
  return (value || '').replace(/\D/g, '');
}

/**
 * Format a phone string for display. Returns a NANP-formatted string when the
 * input looks like a US number (10 digits, or 11 digits starting with 1), and
 * progressively formats partial input as the user types. Anything that doesn't
 * look like a NANP number (e.g. a "+44 …" international number, or > 11 digits)
 * is returned as-typed so it isn't mangled.
 */
export function formatPhoneDisplay(value: string | null | undefined): string {
  if (!value) return '';
  const raw = String(value);
  const startsPlus = raw.trim().startsWith('+');
  const d = digitsOnly(raw);

  // Explicit international (+…) that isn't +1<10digits> → leave it alone.
  if (startsPlus && !(d.length === 11 && d.startsWith('1')) && d.length !== 10) {
    return raw;
  }
  // Too long to be NANP → leave alone.
  if (d.length > 11) return raw;

  let prefix = '';
  let local = d;
  if (d.length === 11 && d.startsWith('1')) {
    prefix = '+1 ';
    local = d.slice(1);
  }

  const a = local.slice(0, 3);
  const b = local.slice(3, 6);
  const c = local.slice(6, 10);

  let out = '';
  if (local.length === 0) out = '';
  else if (local.length <= 3) out = a;
  else if (local.length <= 6) out = `(${a}) ${b}`;
  else out = `(${a}) ${b}-${c}`;

  return (prefix + out).trim();
}

/**
 * Normalize a US number to E.164 (+1XXXXXXXXXX) when it has 10 or 11 digits.
 * Leaves other inputs (already-+prefixed international, partials) as a best-effort
 * "+<digits>" or the trimmed original. Use at submit time when an endpoint wants E.164.
 */
export function toE164US(value: string | null | undefined): string {
  const raw = (value || '').trim();
  const d = digitsOnly(raw);
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith('1')) return `+${d}`;
  if (raw.startsWith('+')) return raw;
  return d ? `+${d}` : '';
}
